// Packages
import React, { useState, useEffect, useRef, useMemo } from "react";
import { cloneDeep } from "lodash";

// Components
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Box, Grid, Tooltip, InputAdornment, SvgIcon } from "@mui/material";

// Img
import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as InfoIcon } from "assets/images/icons/icn_Info_solid.svg";
import { ReactComponent as ScheduleIcon } from "assets/images/icons/icn_table_cell_schedule.svg";
import { ReactComponent as ClockIcon } from "assets/images/icons/icn_display_clock.svg";

// Const
import { defaultDateFormat } from '../..//const';

// DayJS
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const SchedulePicker = ({
  selectedFWList,
  setSelectedFWList,
  demoFW,
  isLengthThanOne,
  isNext
}) => {
  const isInitialized = useRef(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const timeZone = useMemo(() => demoFW?.siteTimezone || "UTC", [demoFW?.siteTimezone]);
  const timeZoneName = useMemo(() => demoFW?.siteTimezoneName || "UTC", [demoFW?.siteTimezoneName]);

  useEffect(() => {
    if (demoFW && !isInitialized.current) {

      const clonedList = selectedFWList.map(fw => {
        const schedule = demoFW?.targetFirmwareSchedule ? dayjs.unix(demoFW.targetFirmwareSchedule).tz(timeZone).startOf('hour') : dayjs().tz(timeZone).startOf('hour');

        return {
          ...cloneDeep(fw),
          targetFirmwareSchedule: schedule.unix(),
          targetFirmwareScheduleDate: schedule.format(defaultDateFormat.all) // YYYY/MM/DD hh:00 A
        }
      })

      setSelectedFWList(clonedList);

      isInitialized.current = true;
    }
  }, [demoFW]);

  return (
    <>

      {
        !isNext &&
        <Grid container gap={1.5} sx={{ margin: "30px 0" }}>

          {
            demoFW &&
            <>
              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={demoFW?.targetFirmwareSchedule ? dayjs.unix(demoFW.targetFirmwareSchedule).tz(timeZone) : dayjs().tz(timeZone)}
                    format={defaultDateFormat.date} // YYYY/MM/DD
                    slots={{
                      openPickerIcon: (props) => (
                        <SvgIcon {...props} component={isDateOpen ? ChevronUp : ChevronDown} inheritViewBox />
                      ),
                    }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        sx: {
                          width: "220px",
                          ".MuiInputBase-root": {
                            height: 35,
                            borderRadius: "8px",
                          },
                          ".MuiInputAdornment-root": {
                            margin: "0",
                          },
                          ".MuiInputAdornment-positionStart": {
                            marginRight: "5px"
                          },
                        },
                        inputProps: {
                          readOnly: true,
                          sx: {
                            cursor: "pointer",
                          }
                        },
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <ScheduleIcon />
                            </InputAdornment>
                          ),
                        },
                        onClick: () => setIsDateOpen((prev) => !prev),
                      },
                      openPickerIcon: {
                        sx: {
                          fontSize: "1.4rem",
                          color: "#007bff",
                          pointerEvents: "none"
                        }
                      }
                    }}
                    open={isDateOpen}
                    onOpen={() => setIsDateOpen(true)}
                    onClose={() => setIsDateOpen(false)}
                    onChange={(newDate) => {
                      const clonedList = selectedFWList.map(fw => {

                        const existsSchedule = demoFW?.targetFirmwareSchedule ? dayjs.unix(demoFW.targetFirmwareSchedule).tz(timeZone) : newDate.tz(timeZone);

                        const mergedSchedule = existsSchedule
                          .year(newDate.year())
                          .month(newDate.month())
                          .date(newDate.date())
                          .startOf('hour');

                        return {
                          ...cloneDeep(fw),
                          targetFirmwareSchedule: mergedSchedule.unix(),
                          targetFirmwareScheduleDate: mergedSchedule.format(defaultDateFormat.all) // YYYY/MM/DD hh:00 A
                        }
                      })

                      setSelectedFWList(clonedList);
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    value={demoFW?.targetFirmwareSchedule ? dayjs.unix(demoFW.targetFirmwareSchedule).tz(timeZone) : dayjs().tz(timeZone)}
                    format={defaultDateFormat.time} // hh:00 A
                    views={['hours']}
                    slots={{
                      openPickerIcon: (props) => (
                        <SvgIcon {...props} component={isTimeOpen ? ChevronUp : ChevronDown} inheritViewBox />
                      ),
                    }}
                    slotProps={{
                      actionBar: { actions: [] }, // prevent displaying "OK" button.
                      textField: {
                        variant: "outlined",
                        sx: {
                          width: "220px",
                          ".MuiInputBase-root": {
                            height: 35,
                            borderRadius: "8px",
                          },
                          ".MuiInputAdornment-root": {
                            margin: "0",
                          },
                          ".MuiInputAdornment-positionStart": {
                            marginRight: "5px"
                          },
                        },
                        inputProps: {
                          readOnly: true,
                          sx: {
                            cursor: "pointer",
                          }
                        },
                        InputProps: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <ClockIcon sx={{ display: 'flex', mr: 1 }} />
                            </InputAdornment>
                          ),
                        },
                        onClick: () => setIsTimeOpen((prev) => !prev),
                      },
                      openPickerIcon: {
                        sx: {
                          fontSize: "1.4rem",
                          color: "#007bff",
                          pointerEvents: "none"
                        }
                      }
                    }}
                    open={isTimeOpen}
                    onOpen={() => setIsTimeOpen(true)}
                    onClose={() => setIsTimeOpen(false)}
                    onChange={(newTime) => {
                      const clonedList = selectedFWList.map(fw => {
                        const existsSchedule = demoFW?.targetFirmwareSchedule ? dayjs.unix(demoFW.targetFirmwareSchedule).tz(timeZone) : newTime.tz(timeZone);
                        const mergedSchedule = existsSchedule
                          .hour(newTime.hour())
                          .startOf('hour')

                        return {
                          ...cloneDeep(fw),
                          targetFirmwareSchedule: mergedSchedule.unix(),
                          targetFirmwareScheduleDate: mergedSchedule.format(defaultDateFormat.all) // YYYY/MM/DD hh:00 A
                        }
                      })

                      setSelectedFWList(clonedList);
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item alignSelf={"center"}>
                {
                  isLengthThanOne &&
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box>Multiple Time Zones</Box>
                    <Box sx={{ margin: "6px 0 0 6px" }}>
                      <Tooltip title="The update time is based on each site's local time.">
                        <InfoIcon />
                      </Tooltip>
                    </Box>
                  </Box>
                }
                {
                !isLengthThanOne &&
                  <>
                    {
                      demoFW?.siteTimezone && demoFW?.siteTimezoneName ?
                      `${timeZoneName} (UTC${timeZone})` :
                      `-`
                    }
                  </>
                }


              </Grid>
            </>
          }

        </Grid>
      }

      {
        isNext &&
        <Grid container gap={0.5} alignContent={"center"} sx={{ margin: "34.4px 0" }}>
          {
            demoFW &&
            <>
              <Grid item><ScheduleIcon /></Grid>
              <Grid item>Scheduled</Grid>
              <Grid item>{
                demoFW?.targetFirmwareSchedule ?
                  dayjs.unix(demoFW.targetFirmwareSchedule).format(defaultDateFormat.all) :
                  "-"
              }</Grid>
              <Grid item> - </Grid>
              {
                demoFW?.siteTimezone && demoFW?.siteTimezoneName ?
                <Grid item> {!isLengthThanOne && `${timeZoneName} (UTC${timeZone})`} </Grid> :
                <Grid item> - </Grid>
              }
            </>

          }
        </Grid>
      }

    </>
  );
};

export default SchedulePicker;